import { useMutation } from '@apollo/client';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import { fieldUpdate } from '@platform-ui-common/hooks/useForm';
import { emailField, fullNameField } from '@platform-ui-common/hooks/useForm/fields.js';
import useForm from '@platform-ui-common/hooks/useForm/hook.js';
import { ValidationMode } from '@platform-ui-common/hooks/useForm/types.js';
import useSnackbar from '@platform-ui-common/hooks/useSnackbar';
import { IdentityPaths } from '@platform-ui/auth/Routes';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import useTenant from '@platform-ui/auth/hooks/useTenant';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Button, Callout, Dialog, Flex, IconButton, Text, TextField } from '@radix-ui/themes';
import { useState } from 'react';
import LinkAnonymousUserMutation from './LinkAnonymousUserMutation.graphql';

const LinkAnonymousAccountModal = () => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');

  const partner = usePartner();
  const { tenantId } = useTenant();
  const notify = useSnackbar();
  const {
    user: { gcip },
    resetAuthState,
  } = useAuth();

  const [linkAnonymousUser, { loading }] = useMutation(LinkAnonymousUserMutation);

  const { dispatch, formData, errors, trySubmit } = useForm(
    {
      name: '',
      email: '',
    },
    {
      name: fullNameField('name'),
      email: emailField(),
    },
    ValidationMode.OnSubmit,
  );

  const { email, name } = formData;

  const handleSubmit = () =>
    trySubmit(async () => {
      try {
        setError('');
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const token = await gcip.getIdToken();

        const response = await linkAnonymousUser({
          variables: {
            input: {
              token,
              tenantId,
              partnerId: partner.id,
              email,
              name,
              timezone,
              continueUrl: `${window.location.origin}/${partner.externalId}${IdentityPaths.MAGIC_LINK}`,
            },
          },
        });

        if (response.data?.identity?.linkAnonymousUser?.metadata?.status === 200) {
          notify.showSnackbar(
            <>
              Your account has been successfully linked to <strong>{email}</strong>. You will
              receive an email with a link to login to your account.
            </>,
          );
          resetAuthState();
          window.location.reload();
        } else {
          setError('Failed to link account. Please try again.');
        }
      } catch (e) {
        console.error('Link account error:', e);
        setError('An error occurred while linking your account.');
      }
    });

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger>
        <Button size="2" variant="outline">
          Link Account
        </Button>
      </Dialog.Trigger>

      <Dialog.Content size="2" maxWidth="460px">
        <Flex justify="between" align="start" mb="2">
          <Dialog.Title>Link Your Account</Dialog.Title>
          <Dialog.Close>
            <IconButton variant="ghost" color="gray" disabled={loading}>
              <Cross2Icon />
            </IconButton>
          </Dialog.Close>
        </Flex>

        <Dialog.Description size="2" mb="4">
          Provide your email and name to convert your guest account into a permanent account. When
          you linked, you will receive an email with a link to login to your account.
        </Dialog.Description>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Flex direction="column" gap="4">
            {error && (
              <Callout.Root color="red" role="alert">
                <Callout.Text>{error}</Callout.Text>
              </Callout.Root>
            )}

            <label htmlFor="name">
              <Text as="div" size="2" mb="1" weight="medium">
                Full Name
              </Text>
              <TextField.Root
                id="name"
                value={name}
                onChange={(e) => dispatch(fieldUpdate('name', e.target.value))}
                placeholder="Enter your full name"
                disabled={loading}
                required
              />
              {errors?.name && (
                <Text size="1" color="red" mt="1">
                  {errors.name}
                </Text>
              )}
            </label>

            <label htmlFor="email">
              <Text as="div" size="2" mb="1" weight="medium">
                Email
              </Text>
              <TextField.Root
                id="email"
                type="email"
                value={email}
                onChange={(e) => dispatch(fieldUpdate('email', e.target.value))}
                placeholder="Enter your email"
                disabled={loading}
                required
              />
              {errors?.email && (
                <Text size="1" color="red" mt="1">
                  {errors.email}
                </Text>
              )}
            </label>

            <Flex gap="3" justify="end" mt="2">
              <Dialog.Close>
                <Button type="button" variant="soft" color="gray" disabled={loading}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button size="2" variant="outline" type="submit" loading={loading}>
                Link Account
              </Button>
            </Flex>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default LinkAnonymousAccountModal;
